import type { DashboardShareEvent, DashboardShareLink } from "../../../domain/dashboard/dashboard-share.js";
import type { DashboardShareRepository } from "../../../repositories/dashboard-share-repository.js";

const LINKS_KEY = "inspect_dashboard_share_links";
const EVENTS_KEY = "inspect_dashboard_share_events";
const MAX_EVENTS = 2000;

function read<T>(key: string, fallback: T): T {
  try {
    if (typeof localStorage === "undefined") return fallback;
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(key, JSON.stringify(value));
}

/** Modo navegador: memória espelhada no localStorage. Servidor: `SqliteDashboardShareRepository`. */
export class InMemoryDashboardShareRepository implements DashboardShareRepository {
  private links: DashboardShareLink[] = read<DashboardShareLink[]>(LINKS_KEY, []);
  private events: DashboardShareEvent[] = read<DashboardShareEvent[]>(EVENTS_KEY, []);

  private reload(): void {
    this.links = read<DashboardShareLink[]>(LINKS_KEY, this.links);
  }

  async insert(link: DashboardShareLink): Promise<void> {
    this.reload();
    if (this.links.some(l => l.id === link.id || l.token === link.token)) throw new Error("dashboard share link already exists");
    this.links = [...this.links, { ...link }];
    write(LINKS_KEY, this.links);
  }

  async findById(id: string): Promise<DashboardShareLink | null> {
    this.reload();
    const found = this.links.find(l => l.id === id);
    return found ? { ...found } : null;
  }

  async findByToken(token: string): Promise<DashboardShareLink | null> {
    this.reload();
    const found = this.links.find(l => l.token === token);
    return found ? { ...found } : null;
  }

  async list(filter: { clientId?: string | undefined; createdBy?: string | undefined }): Promise<DashboardShareLink[]> {
    this.reload();
    return this.links
      .filter(l => (!filter.clientId || l.clientId === filter.clientId) && (!filter.createdBy || l.createdBy === filter.createdBy))
      .map(l => ({ ...l }))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  async updateIfUnchanged(link: DashboardShareLink, expectedUpdatedAt: string): Promise<boolean> {
    this.reload();
    const index = this.links.findIndex(l => l.id === link.id);
    const current = index >= 0 ? this.links[index] : undefined;
    if (!current || current.updatedAt !== expectedUpdatedAt) return false;
    // Mesma regra do trigger SQLite: revogação é definitiva.
    if (current.revokedAt && link.revokedAt !== current.revokedAt) throw new Error("dashboard share revocation is final");
    if (this.links.some(l => l.id !== link.id && l.token === link.token)) return false;
    const next = [...this.links];
    next[index] = { ...link, lastAccessAt: current.lastAccessAt, accessCount: current.accessCount };
    this.links = next;
    write(LINKS_KEY, this.links);
    return true;
  }

  async touchAccess(id: string, at: string, incrementCount: boolean): Promise<void> {
    this.reload();
    this.links = this.links.map(l => (l.id === id ? { ...l, lastAccessAt: at, accessCount: l.accessCount + (incrementCount ? 1 : 0) } : l));
    write(LINKS_KEY, this.links);
  }

  async appendEvent(event: DashboardShareEvent): Promise<void> {
    this.events = read<DashboardShareEvent[]>(EVENTS_KEY, this.events);
    this.events = [...this.events, { ...event }].slice(-MAX_EVENTS);
    write(EVENTS_KEY, this.events);
  }

  async listEvents(linkId: string, limit: number): Promise<DashboardShareEvent[]> {
    this.events = read<DashboardShareEvent[]>(EVENTS_KEY, this.events);
    return this.events.filter(e => e.linkId === linkId).sort((a, b) => b.at.localeCompare(a.at)).slice(0, limit);
  }
}
